'use client';

import { Button, IcArrowDown, IcArrowRight, Image, Typography } from '@musinsa/mds';
import { SERVICE, useDeviceInfo } from '@musinsa/packages-device-detection';
import type {
  GetRecruitmentCampaignsServiceResponse,
  InProgressCampaignItem,
} from '@musinsa/services-curator';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { getPaths, pathsMusinsa, useNavigate } from '../../common';
import { useDdayCountdown } from '../../common/hooks';
import { useCouponCount } from '../../coupon/hooks/useCouponCount';
import { curatorQueries } from '../../query-factory';
import { BRAND_BLUE, BRAND_BLUE_BG, BRAND_BLUE_BORDER } from '../constants/colors';

const VISIBLE_LIMIT = 5;
const MAX_CAMPAIGNS = 10;

const DISPLAY_STATUS_LABELS: Record<InProgressCampaignItem['displayStatus'], string> = {
  UPLOAD_REQUIRED: '업로드 필요',
  SHIPPING: '배송중',
  PREPARING_PRODUCT: '상품 준비중',
};

const CONTENT_TYPE_LABELS: Record<string, string> = {
  IMAGE: '이미지',
  SHORT_FORM: '숏폼 영상',
  LONG_FORM: '롱폼 영상',
  TEXT: '텍스트',
  BLOG_POST: '블로그 포스트',
};

function getCtaLabel(item: InProgressCampaignItem, timeStr: string): string | null {
  if (item.displayStatus !== 'UPLOAD_REQUIRED') return null;
  const deadline = item.contentUploadDeadline;
  if (!deadline?.contentType) return null;
  const typeLabel = CONTENT_TYPE_LABELS[deadline.contentType] ?? deadline.contentType;
  return timeStr ? `${typeLabel} 업로드 (${timeStr} 남음)` : `${typeLabel} 업로드`;
}

const OngoingCampaignRow = ({ item }: { item: InProgressCampaignItem }) => {
  const { service } = useDeviceInfo();
  const { navigate } = useNavigate();
  const paths = getPaths(service);
  // 상품 썸네일 비율 — 29CM=square(72x72) / 무신사=portrait(72x86). 전 지면 공통 정책. (SID-4424)
  const is29cm = service === SERVICE['29CM'];
  const deadlineAt = item.contentUploadDeadline?.deadlineAt;
  // SID-4165: 마감 D-day 라벨은 그대로 노출하되, 기한 경과(isExpired)로 업로드 버튼을
  // 비활성화하지 않는다. 기한이 지나도 리스트 화면에서 콘텐츠 제출은 가능해야 한다.
  const { label: deadlineLabel } = useDdayCountdown(deadlineAt);

  const ctaLabel = getCtaLabel(item, deadlineLabel);

  // 통일 상세 — 진행 중(혼합: 모집/제안)도 campaignId 만으로 진입, 화면이 participationType 으로 분기.
  const handleNavigateToDetail = () => {
    navigate(paths.campaignSeedingDetail({ campaignId: item.campaignId }));
  };

  return (
    <div className="flex flex-col">
      <button
        type="button"
        onClick={handleNavigateToDetail}
        className="flex items-start py-[8px]! text-left w-full cursor-pointer"
      >
        <div
          className={`relative w-[72px] ${is29cm ? 'h-[72px]' : 'h-[86px]'} overflow-hidden rounded-[4px] shrink-0`}
        >
          <Image
            src={item.goodsImage}
            alt={item.goodsName}
            objectFit="cover"
            size="cover"
            direction="horizontal"
            heightFull
          />
        </div>
        <div className="flex-1 min-w-0 self-stretch flex flex-col justify-center pl-[12px] gap-[2px]">
          <Typography variant="body13pxSemi" colorScheme="blue">
            {DISPLAY_STATUS_LABELS[item.displayStatus] ?? item.displayStatus}
          </Typography>
          <Typography variant="body13pxSemi">{item.brandName}</Typography>
          <Typography variant="body13pxReg">{item.goodsName}</Typography>
          {item.selectedOptions && (
            <Typography variant="body13pxReg" colorScheme="gray-600">
              {item.selectedOptions}
            </Typography>
          )}
        </div>
      </button>
      {ctaLabel && (
        <div className="pt-[4px]! pb-[8px]!">
          <Button variant="secondary" size={40} className="w-full" onClick={handleNavigateToDetail}>
            <Typography variant="body14pxMed">{ctaLabel}</Typography>
          </Button>
        </div>
      )}
    </div>
  );
};

const CouponHeroBand = () => {
  const { service } = useDeviceInfo();
  const isMusinsa = service === SERVICE.MUSINSA;

  const { data: userInfo } = useQuery({
    ...curatorQueries.profile.userInfo,
    enabled: isMusinsa,
  });
  const shopTitle = userInfo?.snsTitle ?? '';

  const { totalCount } = useCouponCount({
    shopTitle,
    enabled: isMusinsa && !!shopTitle,
  });

  const { navigate } = useNavigate();

  if (!isMusinsa || !shopTitle || totalCount < 1) return null;

  const handleClick = () => {
    navigate(pathsMusinsa.myShopCoupon({ shopSlug: shopTitle }));
  };

  return (
    <div className="relative px-4 pt-4 pb-1 bg-white">
      <div
        aria-hidden
        className="pointer-events-none absolute right-[54px] top-[13px] z-[2] h-[3px] w-[7px] bg-white"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute right-[54px] bottom-px z-[2] h-[3px] w-[7px] bg-white"
      />
      <div
        role="button"
        tabIndex={0}
        onClick={handleClick}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleClick();
          }
        }}
        className="flex h-10 w-full cursor-pointer items-center rounded border p-0"
        style={{ borderColor: BRAND_BLUE_BORDER, backgroundColor: BRAND_BLUE_BG }}
      >
        <div className="flex min-w-0 flex-1 items-center px-3">
          <Typography variant="body13pxMed">
            브랜드 협업 <span style={{ color: BRAND_BLUE }}>할인 쿠폰 ({totalCount}개)</span>
          </Typography>
        </div>
        <div className="relative flex h-full w-px shrink-0 items-center justify-center before:absolute before:-top-[3px] before:left-1/2 before:-ml-[3px] before:size-[6px] before:rounded-full before:border! before:border-[rgba(36,94,255,0.4)]! before:bg-white! before:content-[''] after:absolute after:-bottom-[3px] after:left-1/2 after:-ml-[3px] after:size-[6px] after:rounded-full after:border! after:border-[rgba(36,94,255,0.4)]! after:bg-white! after:content-['']">
          <div className="h-[30px] border-l! border-dashed! border-[rgba(36,94,255,0.4)]!" />
        </div>
        <div className="flex h-full w-10 shrink-0 items-center justify-center">
          <IcArrowRight width={16} height={16} strokeColor="blue" />
        </div>
      </div>
    </div>
  );
};

export const OngoingCampaignSection = () => {
  const { data, isLoading } = useQuery(curatorQueries.campaigns.inProgress());
  const campaigns = data?.campaigns ?? [];
  const isEmpty = !isLoading && campaigns.length === 0;

  // 빈 상태에서만 보조 신호 조회 — 진행중 캠페인이 있거나 로딩 중이면 미발화(enabled:isEmpty). (CMQA-7210)
  const { data: proposalCount } = useQuery({
    ...curatorQueries.campaigns.seedingMeProposalCount(),
    enabled: isEmpty,
  });
  // recruitment 팩토리는 useInfiniteQuery 용(queryFn 에 pageParam)이라 useQuery 로 쓰면 data 가 {} 로
  // 추론된다. CampaignRecruitmentGrid 와 동일하게 응답 타입을 명시한다.
  const { data: recruitingFirst } = useQuery({
    // 전용 카운트 없음 → page1·size1 list 존재로만 "모집중 캠페인 있음" 판정 (비용 최소화).
    ...curatorQueries.campaigns.recruitment({ size: 1 }),
    enabled: isEmpty,
  });

  const [expanded, setExpanded] = useState(false);
  const { service, isWebview } = useDeviceInfo();
  const { navigate } = useNavigate();
  const paths = getPaths(service);

  // 29CM 웹뷰는 상세를 새 웹뷰 레이어(openWebview)로 띄워 me 페이지가 unmount 되지 않아, 펼침(expanded)
  // 로컬 state 가 복귀 후에도 보존돼 더보기 버튼이 사라진다. 복귀(visible) 시 접힘으로 리셋. (CMQA-7180)
  // 웹/무신사 SPA 는 router.push remount 로 자가 리셋되므로 isWebview 게이트(웹 탭전환 과접힘 방지).
  useEffect(() => {
    if (!isWebview) return;
    const onVisible = () => {
      if (document.visibilityState === 'visible') setExpanded(false);
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [isWebview]);

  if (isLoading) return null;

  const visible = expanded ? campaigns.slice(0, MAX_CAMPAIGNS) : campaigns.slice(0, VISIBLE_LIMIT);
  const showMore = !expanded && campaigns.length > VISIBLE_LIMIT;

  const handleViewCampaigns = () => {
    navigate(paths.campaignRecruitment());
  };
  const handleViewProposals = () => {
    // 제안 받은(시딩) 탭으로 딥링크 — CollaborationPageView 가 ?type / ?category 로 탭 선택.
    navigate(`${paths.campaignCollaboration()}?type=BRANDS_PROPOSAL&category=SEEDING`);
  };

  // 빈 상태 보조 안내 3분기 (우선순위: 제안 > 모집 > plain).
  // (CMQA-7210, Figma 제안 1539-34704 / 모집 1543-34724 / plain 1539-34684)
  const hasProposal = (proposalCount?.pending ?? 0) > 0;
  const hasRecruiting =
    ((recruitingFirst as GetRecruitmentCampaignsServiceResponse | undefined)?.list.length ?? 0) > 0;
  const emptyGuide = hasProposal
    ? {
        text: '브랜드가 제안한 캠페인을 확인해 보세요.',
        ctaLabel: '제안 보기',
        onCta: handleViewProposals,
      }
    : hasRecruiting
      ? {
          text: '모집 중인 캠페인에 참여해 보세요.',
          ctaLabel: '캠페인 보기',
          onCta: handleViewCampaigns,
        }
      : null;

  return (
    <section className="w-full bg-white pb-[24px]!">
      <div className="flex items-end pb-[8px]! pt-[24px]! px-[16px]!">
        <Typography variant="title18pxMed">진행 중 캠페인</Typography>
      </div>
      {isEmpty ? (
        <div className="flex flex-col items-center justify-center gap-[12px]! py-[40px]! px-[16px]!">
          <div className="flex flex-col items-center gap-[2px]!">
            <Typography size="14" weight="medium">
              진행 중 캠페인이 없습니다.
            </Typography>
            {emptyGuide && (
              <Typography variant="body13pxReg" colorScheme="gray-600">
                {emptyGuide.text}
              </Typography>
            )}
          </div>
          {emptyGuide && (
            <Button
              variant="tertiaryOutlinedGray"
              className="h-6 px-1.5"
              onClick={emptyGuide.onCta}
            >
              <Typography variant="body13pxMed">{emptyGuide.ctaLabel}</Typography>
            </Button>
          )}
        </div>
      ) : (
        <>
          <CouponHeroBand />
          <div className="flex flex-col px-[16px]!">
            {visible.map((item) => (
              <OngoingCampaignRow key={item.participationId} item={item} />
            ))}
          </div>
          {showMore && (
            <div className="px-[16px]! pt-[12px]!">
              <Button
                variant="secondary"
                size={40}
                className="w-full"
                onClick={() => setExpanded(true)}
              >
                <Typography variant="body14pxMed">
                  더보기({VISIBLE_LIMIT}/{campaigns.length})
                </Typography>
                <IcArrowDown width={16} height={16} strokeColor="gray-600" className="ml-[2px]" />
              </Button>
            </div>
          )}
        </>
      )}
    </section>
  );
};
